import { ReadResult } from 'interfaces/queryOptions'
import { Client, Installment, Order, Plan, Role } from 'models'
import { Coupon } from 'models/coupon'
import { patch } from 'services/billing'
import { checkRoles, cleanAmount } from 'utils'
import { edit as baseEdit } from 'services/billing'

export type EditSubscription = {
  subscription: ReadResult<Order>
  client: ReadResult<Client>
  accessPlan: ReadResult<Plan>
  coupon?: ReadResult<Coupon>
  installments?: Installment[]
}

export async function approve(
  id: number | string,
  approvedAt: Date
): Promise<any> {
  await checkRoles([Role.MANAGER, Role.FINANCE])
  const { data } = await patch(`/subscriptions/${id}/update-status/approved`, {
    data: { status_updated_at: approvedAt }
  })
  return data
}

export async function activate(
  id: number | string,
  activatedAt: Date
): Promise<any> {
  await checkRoles([Role.MANAGER, Role.FINANCE])
  const { data } = await patch(`/subscriptions/${id}/activate`, {
    data: { status_updated_at: activatedAt }
  })
  return data
}

export async function markAsPending(
  id: number | string,
  pendingAt: Date
): Promise<any> {
  await checkRoles([Role.MANAGER, Role.FINANCE])
  const { data } = await patch(`/subscriptions/${id}/update-status/pending`, {
    data: { status_updated_at: pendingAt }
  })
  return data
}

export async function cancel(
  id: number | string,
  canceledAt?: Date
): Promise<any> {
  await checkRoles([Role.MANAGER, Role.FINANCE])
  const attributes = {
    ...(canceledAt && { closed_at: canceledAt })
  }
  const payload = Object.keys(attributes).length > 0 ? { attributes } : {}
  const { data } = await patch(`/subscriptions/${id}/cancel`, { data: payload })
  return data
}

const transformAttributes = (
  attributes: Record<string, any>
): Record<string, any> => {
  const { minimum_invoice_amount, fine_mode, fine_amount } = attributes

  return {
    minimum_invoice_amount:
      minimum_invoice_amount === ''
        ? null
        : cleanAmount(minimum_invoice_amount),
    fine_mode: fine_amount === '' || fine_amount === 0 ? null : fine_mode,
    fine_amount:
      fine_amount === '' ? 0 : fine_mode === null ? 0 : cleanAmount(fine_amount)
  }
}

export async function edit(
  id: number,
  { subscription, client, accessPlan, coupon, installments }: EditSubscription
): Promise<ReadResult<Order>> {
  const { fine_mode, fine_amount, minimum_invoice_amount } =
    transformAttributes(subscription.attributes)

  const payload: Partial<ReadResult<Order>> = {
    attributes: {
      ordered_at: subscription.attributes.ordered_at,
      amount: subscription.attributes.amount,
      quantity: subscription.attributes.quantity,
      fine_mode,
      fine_amount,
      minimum_invoice_amount,
      setup_fee: cleanAmount(subscription.attributes.setup_fee)
    },
    relationships: {
      client: {
        type: 'client',
        id: client.id
      },
      plan: {
        type: 'plan',
        id: accessPlan.id
      }
    }
  }

  if (coupon) {
    payload.relationships!.coupon = {
      type: 'coupon',
      id: coupon.id
    }
  }

  if (installments != null) {
    payload.relationships!.installments = installments.map((inst) => ({
      type: 'order_installments',
      attributes: {
        type: inst.type,
        amount:
          typeof inst.amount === 'string'
            ? cleanAmount(inst.amount as string)
            : inst.amount,
        due_at: inst.due_at,
        upfront: inst.upfront,
        eligible: inst.eligible
      }
    }))
  }

  const response = await baseEdit<Order>('/subscriptions', id, payload)
  return response
}
